import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  computed,
  inject,
  afterNextRender,
  viewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoPipe } from '@jsverse/transloco';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { TableEmptyStateComponent } from '../table/table-empty-state/table-empty-state.component';

@Component({
  selector: 'lib-long-text-view',
  imports: [TranslocoPipe, ChevronDownIconComponent, TableEmptyStateComponent],
  templateUrl: './long-text-view.component.html',
  styleUrl: './long-text-view.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LongTextViewComponent implements AfterViewInit {
  contentContainer = viewChild<ElementRef<HTMLDivElement>>('contentContainer');

  private readonly sanitizer = inject(DomSanitizer);

  content = input.required<string>();
  collapsedHeight = input<string>('8rem');
  seeMoreLabel = input<string>('discussion_section_see_more_button');
  seeLessLabel = input<string>('discussion_section_see_less_button');

  isCollapsed = signal<boolean>(true);
  needsTruncation = signal<boolean>(false);
  fullContentHeight = signal<string>('0px');

  isHtml = computed(() => {
    const html = this.content();
    if (!html || typeof html !== 'string') {
      return false;
    }
    return /<[^>]+>/g.test(html);
  });

  sanitizedContent = computed(() => {
    const html = this.content();
    if (!html || typeof html !== 'string' || !this.isHtml()) {
      return '';
    }
    return this.isHtml() ? this.sanitizer.bypassSecurityTrustHtml(html) : html;
  });

  constructor() {
    afterNextRender(() => {
      this.checkContentHeight();
    });
  }

  ngAfterViewInit(): void {
    this.checkContentHeight();
  }

  private checkContentHeight() {
    const container = this.contentContainer();
    if (!container?.nativeElement) {
      return;
    }
    const element = container.nativeElement;
    const collapsedMaxHeight = this.parseHeight(this.collapsedHeight());
    // Temporarily remove max-height and get computed styles to get the full content height
    const originalMaxHeight = element.style.maxHeight;
    const originalOverflow = element.style.overflow;

    element.style.maxHeight = 'none';
    element.style.overflow = 'visible';

    // Force reflow to get accurate measurements
    const scrollHeight = element.scrollHeight;

    // Store the full content height in pixels for smooth animation
    this.fullContentHeight.set(`${scrollHeight + 4}px`);
    // Restore original styles
    element.style.maxHeight = originalMaxHeight;
    element.style.overflow = originalOverflow;

    // Show button if content height exceeds collapsed height
    const needsTruncation = scrollHeight > collapsedMaxHeight;
    this.needsTruncation.set(needsTruncation);
  }

  private parseHeight(height: string): number {
    // Convert rem/px to pixels (assumes 16px base for rem)
    if (height.endsWith('rem')) {
      return Number.parseFloat(height) * 16;
    } else if (height.endsWith('px')) {
      return Number.parseFloat(height);
    }
    return 80; // Default fallback (5rem * 16px)
  }

  toggle() {
    this.isCollapsed.update(value => !value);
  }
}
